import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { FileSpreadsheet, AlertTriangle, Columns3 } from 'lucide-react';

export const FileDrop = ({ label, hint, accept, file, onFile, testId }) => (
  <label className="block cursor-pointer" data-testid={testId}>
    <div className={`border-2 border-dashed rounded-lg p-6 text-center transition ${file ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`}>
      <FileSpreadsheet className={`w-8 h-8 mx-auto mb-2 ${file ? 'text-emerald-600' : 'text-slate-400'}`} />
      <p className="text-sm font-medium text-slate-700">{label}</p>
      <p className="text-xs text-slate-500 mt-1">{file ? file.name : hint}</p>
      <input type="file" accept={accept} className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} data-testid={`${testId}-input`} />
    </div>
  </label>
);

export const StatCard = ({ label, value, tone = 'text-slate-900', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-6">
      <p className="text-xs uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-2xl font-semibold ${tone}`}>{value ?? 0}</p>
    </CardContent>
  </Card>
);

// Layout drift: WinTeam adding, removing or reordering columns.
export const HeaderReport = ({ report }) => {
  if (!report) return null;
  const clean = report.layout_matches;
  return (
    <Card data-testid="import-header-report">
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-2">
          <Columns3 className="w-4 h-4 text-slate-400" />
          <p className="font-medium">
            Layout: {report.received_columns} columns received, {report.expected_columns} expected
          </p>
          <Badge variant={clean ? 'secondary' : 'destructive'} data-testid="import-layout-badge">
            {clean ? 'Exact match' : 'Changed'}
          </Badge>
        </div>
        {!clean && (
          <div className="text-sm text-slate-600 space-y-1">
            {report.missing_columns?.length > 0 && (
              <p data-testid="import-missing-columns">
                <span className="font-medium text-amber-700">Missing:</span> {report.missing_columns.join(', ')}
              </p>
            )}
            {report.new_columns?.length > 0 && (
              <p data-testid="import-new-columns">
                <span className="font-medium text-amber-700">New:</span>{' '}
                {report.new_columns.map(c => `${c.header} (${c.populated_rows} rows with data)`).join(', ')}
              </p>
            )}
            {!report.missing_columns?.length && !report.new_columns?.length && (
              <p>Same columns, different order — imported by column name, so nothing is lost.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Ignored columns that arrived with data they didn't carry in the sample export.
export const FlagTable = ({ flags }) => {
  if (!flags?.length) {
    return (
      <p className="text-sm text-slate-400 py-6 text-center" data-testid="import-no-flags">
        No unused column has new data — nothing to review.
      </p>
    );
  }
  return (
    <Card><CardContent className="p-0">
      <Table data-testid="import-flags-table">
        <TableHeader><TableRow>
          <TableHead className="w-20">Column</TableHead>
          <TableHead>Field</TableHead>
          <TableHead className="text-right">Rows with data</TableHead>
          <TableHead>Sample value</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {flags.map((f, i) => (
            <TableRow key={`${f.header}-${i}`} data-testid={`import-flag-${f.header}`}>
              <TableCell className="font-mono text-xs">{f.column || '—'}</TableCell>
              <TableCell className="font-medium">{f.header}</TableCell>
              <TableCell className="text-right">{f.populated_rows}</TableCell>
              <TableCell className="text-slate-600 max-w-xs truncate">{f.sample_value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent></Card>
  );
};

export const ReasonList = ({ rows, emptyText, testId }) => {
  if (!rows?.length) {
    return <p className="text-sm text-slate-400 py-6 text-center">{emptyText}</p>;
  }
  return (
    <Card><CardContent className="p-4 space-y-2" data-testid={testId}>
      {rows.slice(0, 500).map((r, i) => (
        <div key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            <span className="font-mono text-xs mr-2">{r.employee_number || '—'}</span>
            <span className="font-medium">{r.name}</span>
            <Badge variant="outline" className="ml-2 text-xs">{r.reason}</Badge>
            {r.row_index && <span className="text-xs text-slate-500 ml-2">row {r.row_index}</span>}
            {r.detail && <p className="text-xs text-slate-600 mt-0.5">{r.detail}</p>}
          </div>
        </div>
      ))}
      {rows.length > 500 && (
        <p className="text-xs text-slate-500">Showing the first 500 of {rows.length} rows.</p>
      )}
    </CardContent></Card>
  );
};
